/**
 * SectionQuickChips — Sprint L
 *
 * Horizontal-scroll chip list of sections showing live availability status.
 * Tapping a chip drills into that section's seat map. Mirrors the overview
 * map colours so the buyer can also drill down without hunting for the
 * block visually (great for accessibility + tiny mobile viewports).
 */
import React from "react";
import { centsToAUD } from "../lib/events";

const STATUS_TONE = {
  available: {
    dot: "bg-emerald-500",
    label: "Available",
    ring: "border-[var(--gt-line)] hover:border-[var(--gt-ink)]",
  },
  selling_fast: {
    dot: "bg-amber-500",
    label: "Selling fast",
    ring: "border-amber-400 hover:border-amber-500",
  },
  almost_sold: {
    dot: "bg-orange-500",
    label: "Almost sold",
    ring: "border-orange-400 hover:border-orange-500",
  },
  sold_out: {
    dot: "bg-neutral-400",
    label: "Sold out",
    ring: "border-[var(--gt-line)] opacity-60 cursor-not-allowed",
  },
};

export default function SectionQuickChips({
  availability = [],
  onSelectSection,
  activeSection = null,
  selectedSectionCounts = {},
  decorativeNames = [],
}) {
  const decorSet = new Set(decorativeNames);
  const rows = (availability || [])
    .filter((a) => !decorSet.has(a.section))
    .sort((a, b) => a.section.localeCompare(b.section));
  if (rows.length === 0) return null;

  return (
    <div
      className="mt-4 -mx-6 sm:mx-0 overflow-x-auto pb-2"
      data-testid="section-quick-chips"
    >
      <div className="flex gap-2 px-6 sm:px-0 min-w-max">
        {rows.map((a) => {
          const tone = STATUS_TONE[a.status] || STATUS_TONE.available;
          const isActive = activeSection === a.section;
          const isSoldOut = a.status === "sold_out";
          const picked = selectedSectionCounts[a.section] || 0;
          return (
            <button
              key={a.section}
              type="button"
              onClick={() => !isSoldOut && onSelectSection?.(a.section)}
              disabled={isSoldOut}
              aria-label={`${a.section} — ${tone.label}${a.min_price_cents ? `, from ${centsToAUD(a.min_price_cents)}` : ""}`}
              data-testid={`section-chip-${a.section}`}
              data-status={a.status}
              aria-pressed={isActive}
              className={
                "shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-sm border bg-white text-left transition-colors " +
                tone.ring +
                (isActive ? " ring-2 ring-[var(--gt-ink)] border-[var(--gt-ink)]" : "")
              }
            >
              <span
                className={"w-1.5 h-1.5 rounded-full " + tone.dot}
                aria-hidden="true"
              />
              <div className="flex flex-col items-start leading-tight">
                <span className="text-xs font-bold uppercase tracking-wider">
                  {a.section}
                </span>
                <span className="text-[10px] text-[var(--gt-ink-3)]">
                  {isSoldOut
                    ? "Sold out"
                    : a.min_price_cents != null
                    ? `From ${centsToAUD(a.min_price_cents)} · Available`
                    : "Available"}
                </span>
              </div>
              {picked > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-[var(--gt-accent)] text-white text-[10px] font-bold"
                  data-testid={`section-chip-count-${a.section}`}
                >
                  {picked}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
